import { useState } from 'react'
import { Badge, Card, DataState, PageHeader } from '@/components/Common'
import { CheckField, Field, FixedRule, FormSection, TextField } from '@/components/Form'
import { ConfirmDialog, Modal } from '@/components/Modal'
import { useAuth } from '@/lib/auth'
import { useFetch } from '@/lib/hooks'
import { useResource } from '@/lib/resource'
import type { Device, Listener, Reference } from '@/lib/api'

const EMPTY = {
  name: '',
  ae_title: 'MWLSCP',
  description: '',
  listen_address: '0.0.0.0',
  listen_port: 11113,
  max_associations: 32,
  worker_count: 8,
  max_pdu: 131072,
  acse_timeout_seconds: 15,
  dimse_timeout_seconds: 30,
  accept_echo: true,
  accept_find: true,
  restrict_to_listed_aes: false,
  query_policy: 'registered' as 'open' | 'registered' | 'listed',
  enabled: true,
  listed_device_ids: [] as number[],
}

type Draft = typeof EMPTY

/**
 * The ports the daemon answers on.
 *
 * **The port is the identity**, not the AE title: the same title answering on
 * several ports is normal, and inside an accepted association the port is the
 * only thing a worker can use to find its own rules.
 */
export function Listeners() {
  const listeners = useResource<Listener>('/api/v1/listeners')
  const devices = useFetch<Device[]>('/api/v1/devices')
  const reference = useFetch<Reference>('/api/v1/system/reference')
  const { can } = useAuth()

  const [editing, setEditing] = useState<Listener | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Listener | null>(null)
  const [rebound, setRebound] = useState<string | null>(null)

  const mayEdit = can('config_admin')
  const list = listeners.data ?? []

  return (
    <>
      <PageHeader
        title="Listeners"
        subtitle="Where modalities connect. The port is the identity."
        actions={
          mayEdit && (
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              Add listener
            </button>
          )
        }
      />

      {listeners.saveError && <div className="notice notice-error">{listeners.saveError}</div>}
      {rebound && <div className="notice notice-info">{rebound}</div>}

      <Card>
        <p className="card-note">
          Changing a port, an AE title or a timeout makes the daemon stop that
          listener and bind a replacement — every other listener keeps serving
          throughout. Changing the accept flags, the query policy or the
          allow-list needs no rebind at all: those are read per association.
        </p>

        <DataState
          loading={listeners.loading}
          error={listeners.error}
          empty={list.length === 0}
          emptyMessage="No listeners. The daemon refuses to start without one."
        />

        {list.length > 0 && (
          <div className="table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Listener</th>
                  <th>Port</th>
                  <th>AE title</th>
                  <th>Answers</th>
                  <th>Who may query</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((listener) => (
                  <tr key={listener.id} className={listener.enabled ? undefined : 'row-muted'}>
                    <td>
                      <strong>{listener.name}</strong>
                      {!listener.enabled && <> <Badge state="DISABLED" /></>}
                      {listener.description && (
                        <div className="hint">{listener.description}</div>
                      )}
                      {listener.listen_address !== '0.0.0.0' && (
                        <div className="warn-text">
                          configured for {listener.listen_address}, but binds every
                          interface — see below
                        </div>
                      )}
                    </td>
                    <td className="num mono">
                      {listener.listen_port}
                      {listener.listen_port < 1024 && (
                        <div className="hint">privileged</div>
                      )}
                    </td>
                    <td className="mono">{listener.ae_title}</td>
                    <td>
                      {listener.accept_echo && <span className="tag">C-ECHO</span>}{' '}
                      {listener.accept_find ? (
                        <span className="tag">Worklist</span>
                      ) : (
                        <span className="warn-text">worklist refused</span>
                      )}
                    </td>
                    <td>
                      <span className="hint">{listener.query_policy}</span>
                      {listener.restrict_to_listed_aes && (
                        <div className="hint">
                          {listener.listed_device_ids.length === 0 ? (
                            <span className="warn-text">
                              restricted with nothing listed — refuses everyone
                            </span>
                          ) : (
                            `${listener.listed_device_ids.length} device(s) admitted`
                          )}
                        </div>
                      )}
                    </td>
                    <td className="actions">
                      {mayEdit && (
                        <>
                          <button className="btn btn-small" onClick={() => setEditing(listener)}>
                            Edit
                          </button>
                          <button
                            className="btn btn-small btn-danger-quiet"
                            onClick={() => setDeleting(listener)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="table-footnote">
          <strong>The bind address is not enforced.</strong> DCMTK's networking
          takes a port and no address, so every listener answers on every
          interface whatever is configured here. The field is kept because it is
          in the schema, and the console says this rather than implying a
          restriction that is not in force. Restrict at the firewall.
        </p>
      </Card>

      {editing && (
        <ListenerEditor
          listener={editing === 'new' ? null : editing}
          devices={devices.data ?? []}
          policies={reference.data?.query_policies ?? {}}
          saving={listeners.saving}
          error={listeners.saveError}
          onClose={() => {
            setEditing(null)
            listeners.clearError()
          }}
          onSave={async (draft) => {
            const saved =
              editing === 'new'
                ? await listeners.create(draft)
                : await listeners.update(editing.id, draft)
            if (saved) {
              const fields = (saved as Listener).rebind_required ?? []
              setRebound(
                fields.length
                  ? `Saved. The daemon will restart the listener on port ${
                      (saved as Listener).listen_port
                    } within a couple of seconds (${fields.join(', ')} changed). Other listeners are unaffected.`
                  : 'Saved. This takes effect on the next association — no rebind needed.',
              )
              setEditing(null)
            }
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete listener '${deleting.name}'?`}
          confirmLabel="Delete listener"
          destructive
          busy={listeners.saving}
          error={listeners.saveError}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            if (await listeners.remove(deleting.id)) setDeleting(null)
          }}
        >
          <p>
            Every device configured to reach port {deleting.listen_port} will get
            connection-refused, with nothing in our log to explain it.
          </p>
          <p className="hint">
            Refused if it is the only enabled listener, or if any rule is limited
            to this port.
          </p>
        </ConfirmDialog>
      )}
    </>
  )
}

function ListenerEditor({
  listener,
  devices,
  policies,
  saving,
  error,
  onClose,
  onSave,
}: {
  listener: Listener | null
  devices: Device[]
  policies: Record<string, string>
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: Draft) => void
}) {
  const [draft, setDraft] = useState<Draft>(() =>
    listener
      ? {
          name: listener.name,
          ae_title: listener.ae_title,
          description: listener.description,
          listen_address: listener.listen_address,
          listen_port: listener.listen_port,
          max_associations: listener.max_associations,
          worker_count: listener.worker_count,
          max_pdu: listener.max_pdu,
          acse_timeout_seconds: listener.acse_timeout_seconds,
          dimse_timeout_seconds: listener.dimse_timeout_seconds,
          accept_echo: listener.accept_echo,
          accept_find: listener.accept_find,
          restrict_to_listed_aes: listener.restrict_to_listed_aes,
          query_policy: listener.query_policy,
          enabled: listener.enabled,
          listed_device_ids: listener.listed_device_ids,
        }
      : EMPTY,
  )

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const toggleDevice = (id: number, on: boolean) =>
    setDraft((d) => ({
      ...d,
      listed_device_ids: on
        ? [...new Set([...d.listed_device_ids, id])]
        : d.listed_device_ids.filter((x) => x !== id),
    }))

  const needsList = draft.restrict_to_listed_aes || draft.query_policy === 'listed'

  return (
    <Modal
      title={listener ? `Edit '${listener.name}'` : 'Add listener'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving || !draft.name || !draft.ae_title}
            onClick={() => onSave(draft)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}

      <FormSection title="Socket" description="Changing any of these rebinds this listener.">
        <div className="form-grid">
          <TextField label="Name" value={draft.name} onChange={(v) => set('name', v)} required />
          <TextField
            label="AE title"
            value={draft.ae_title}
            onChange={(v) => set('ae_title', v)}
            maxLength={16}
            required
            showCount
            hint="What we call ourselves. A device's Called AE Title is never checked against it — that is a common source of integration pain and this server does not add to it."
          />
          <Field
            label="Port"
            hint="The listener's identity. Unique across listeners. Below 1024 needs the capability the systemd unit grants."
          >
            <input
              type="number"
              value={draft.listen_port}
              onChange={(e) => set('listen_port', Number(e.target.value))}
            />
          </Field>
          <TextField
            label="Bind address"
            value={draft.listen_address}
            onChange={(v) => set('listen_address', v)}
            hint="Not enforced — every listener binds all interfaces. Kept for the record; restrict at the firewall."
          />
        </div>
        <TextField
          label="Description"
          value={draft.description}
          onChange={(v) => set('description', v)}
          maxLength={200}
        />
        <CheckField label="Enabled" checked={draft.enabled} onChange={(v) => set('enabled', v)} />
      </FormSection>

      <FormSection
        title="What it answers"
        tone="inbound"
        description="Read per association, so a change here needs no rebind."
      >
        <CheckField
          label="Verification (C-ECHO)"
          detail="How an engineer proves the link works. Worth leaving on even while a device has no worklist rights — it separates 'wrong address' from 'not permitted'."
          checked={draft.accept_echo}
          onChange={(v) => set('accept_echo', v)}
        />
        <CheckField
          label="Modality Worklist (C-FIND)"
          detail="Off refuses the Worklist presentation context, so no rule is ever reached on this port."
          checked={draft.accept_find}
          onChange={(v) => set('accept_find', v)}
        />
        <FixedRule
          label="Storage is never accepted"
          detail="Not a setting. A worklist server that takes images is a misconfiguration, so the contexts are not advertised at all — a device proposing Storage here is told so at negotiation."
        />
      </FormSection>

      <FormSection title="Who may query" tone="inbound">
        <Field label="Query policy">
          <select
            value={draft.query_policy}
            onChange={(e) =>
              set('query_policy', e.target.value as 'open' | 'registered' | 'listed')
            }
          >
            {Object.entries(policies).map(([key, description]) => (
              <option key={key} value={key}>
                {key} — {description}
              </option>
            ))}
          </select>
          <span className="field-hint">
            ANDed with the device's own permission; the narrower wins. Defaults
            to <strong>registered</strong> because a worklist query releases a
            schedule full of patient names.
          </span>
        </Field>

        <CheckField
          label="Only listed devices may associate at all"
          detail="Stronger than the query policy: an unlisted caller gets nothing, not even C-ECHO. An empty list then refuses everyone, which is a legitimate way to close a port without deleting it."
          checked={draft.restrict_to_listed_aes}
          onChange={(v) => set('restrict_to_listed_aes', v)}
        />

        {needsList && (
          <>
            <div className="subhead">Admitted devices</div>
            {draft.listed_device_ids.length === 0 && (
              <div className="notice notice-warn">
                Nothing listed. This listener currently refuses every caller.
              </div>
            )}
            <div className="services">
              {devices.map((device) => (
                <CheckField
                  key={device.id}
                  label={`${device.name} (${device.ae_title})`}
                  detail={device.source_ip || 'any address'}
                  checked={draft.listed_device_ids.includes(device.id)}
                  onChange={(on) => toggleDevice(device.id, on)}
                />
              ))}
            </div>
          </>
        )}
      </FormSection>

      <FormSection
        title="Limits"
        description="A worklist association is short — connect, one query, release — so these are tighter than an archive's would be."
      >
        <div className="form-grid">
          <Field
            label="Maximum concurrent associations"
            hint="Each one is served by its own worker thread. This is the number actually enforced."
          >
            <input
              type="number"
              value={draft.max_associations}
              onChange={(e) => set('max_associations', Number(e.target.value))}
            />
          </Field>
          <Field label="Worker count">
            <input
              type="number"
              value={draft.worker_count}
              onChange={(e) => set('worker_count', Number(e.target.value))}
            />
          </Field>
          <Field label="ACSE timeout (s)">
            <input
              type="number"
              value={draft.acse_timeout_seconds}
              onChange={(e) => set('acse_timeout_seconds', Number(e.target.value))}
            />
          </Field>
          <Field label="DIMSE timeout (s)">
            <input
              type="number"
              value={draft.dimse_timeout_seconds}
              onChange={(e) => set('dimse_timeout_seconds', Number(e.target.value))}
            />
          </Field>
          <Field label="Maximum PDU">
            <input
              type="number"
              value={draft.max_pdu}
              onChange={(e) => set('max_pdu', Number(e.target.value))}
            />
          </Field>
        </div>
      </FormSection>
    </Modal>
  )
}
